import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  ArrowRight,
  BookOpen,
  Check,
  Infinity as InfinityIcon,
  Monitor,
  MonitorPlay,
  User,
  type LucideIcon,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import SEO from "@/components/SEO";
import MarketTicker from "@/components/MarketTicker";
import { buildAttribution, deliverLeadToCrm } from "@/lib/crmLead";
import Reveal from "@/components/Reveal";
import VideoTestimonials from "@/components/VideoTestimonials";
import { GoogleGlyph, GoogleReviewMarquee } from "@/components/GoogleReviewCard";
import { GOOGLE_REVIEWS } from "@/data/lpReviews";
import { usePublishedTestimonials } from "@/hooks/useTestimonials";
import bullioHero from "@/assets/bullio-lp-hero.webp";
import bullioHero360 from "@/assets/bullio-lp-hero-360.webp";
// The logo is shown at 36px; the 96px copy covers a 2.5x screen.
import bullioFace from "@/assets/bullio-lp-face-sm.webp";
// Stand-in from the client's About artwork until the hero photo in the new
// design is supplied. Replace the file at this path; nothing else changes.
import heroLearner from "@/assets/lp-hero-learner.webp";
import heroLearner480 from "@/assets/lp-hero-learner-480.webp";
import heroLearner680 from "@/assets/lp-hero-learner-680.webp";

/**
 * /lp — the standalone landing page for paid campaigns.
 *
 *   * No site navbar or footer (`isChromelessRoute` strips them). The header
 *     links only scroll within this page, so nothing leads off it.
 *   * One conversion: the lead form at the bottom. Every button scrolls to it,
 *     and a sticky button follows the visitor down the page.
 *   * `noindex`, so it never competes with the home page in search.
 *
 * Copy and layout follow the client's October 2026 design. The colours here
 * (deep green, rust buttons, cream) are this page's own, per that design.
 */

const FORM_ID = "lead-form";

/** Matches the `experience_level` enum in the database. */
const EXPERIENCE_OPTIONS = [
  {
    value: "complete_beginner",
    label: "Complete beginner",
    hint: "I have never placed a trade",
  },
  {
    value: "some_knowledge",
    label: "Some knowledge",
    hint: "I understand the basics, no structure yet",
  },
  {
    value: "intermediate",
    label: "Intermediate",
    hint: "I trade already and want to learn it properly",
  },
  {
    value: "advanced",
    label: "Advanced",
    hint: "I have a process and want to sharpen it",
  },
] as const;

type ExperienceValue = (typeof EXPERIENCE_OPTIONS)[number]["value"];

const highlights: { icon: LucideIcon; title: string; desc: string }[] = [
  { icon: BookOpen, title: "300+ hours", desc: "Courses, lectures & masterclasses" },
  { icon: MonitorPlay, title: "Live rooms", desc: "Market examples explained" },
  { icon: Monitor, title: "Demo practice", desc: "Virtual money, real learning" },
  { icon: User, title: "Your advisor", desc: "Learning & setup guidance" },
  { icon: InfinityIcon, title: "Lifetime content", desc: "Current & future content" },
];

type Topic = { title: string; desc: string; art: "ai" | "day" | "options" | "futures" | "risk"; tone: "navy" | "green" };
const topics: Topic[] = [
  { title: "AI Trading", desc: "Understand AI tools, test ideas and check their limits.", art: "ai", tone: "navy" },
  { title: "Day Trading", desc: "Learn how trades within the same day work.", art: "day", tone: "green" },
  { title: "Options Trading", desc: "Calls, puts and the risks, explained step by step.", art: "options", tone: "navy" },
  { title: "Futures Trading", desc: "Understand contracts, markets and how to practise.", art: "futures", tone: "navy" },
  { title: "Risk Management", desc: "Learn trade sizing and how to plan your risk.", art: "risk", tone: "green" },
];

const advisorPoints = [
  "A learning path based on your experience and goals.",
  "Help with your platform, indicators and setup.",
  "Understand what to compare when choosing a broker.",
  "Ask questions as you learn during support hours.",
];

const nextSteps = [
  { title: "Start with what you know", desc: "Lessons chosen for your level." },
  { title: "Understand the risk", desc: "Prepare before demo practice." },
  { title: "Practise and ask questions", desc: "Choose your next lesson together." },
];

const callSteps = [
  { title: "Request your call", desc: "Leave your details." },
  { title: "Tell us about you", desc: "Your experience and goals." },
  { title: "Review the paid offer", desc: "See the price and terms." },
  { title: "Choose to enroll", desc: "Start your learning path." },
];

const faqs = [
  { q: "Do I need experience?", a: "No. Start with the foundations, or build on what you already know." },
  { q: "Does AI trade for me?", a: "AI Trading is an education topic, not a promise of automatic profits." },
  { q: "Is the call free?", a: "Yes. The call is free. Joining the academy is a separate paid choice." },
  { q: "What does lifetime access cover?", a: "Current and future content. Support and live rooms follow package terms." },
];

const scrollToId = (id: string) => {
  const el = document.getElementById(id);
  if (!el) return;
  const reduced =
    typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
  el.scrollIntoView({ behavior: reduced ? "auto" : "smooth", block: "start" });
};

const Kicker = ({ children, light = false }: { children: React.ReactNode; light?: boolean }) => (
  <p className={`text-[11px] font-semibold uppercase tracking-[0.14em] ${light ? "text-white/70" : "text-[#173f33]"}`}>
    {children}
  </p>
);

/**
 * The drawn header of each topic card, so it stays sharp at any size. Each
 * one loops a small animation (classes in index.css), switched off for
 * visitors who ask for reduced motion.
 */
const TopicArt = ({ art }: { art: Topic["art"] }) => {
  const stroke = "rgba(255,255,255,0.75)";
  const delay = (s: number) => ({ animationDelay: `${s}s` });
  if (art === "ai")
    return (
      <svg viewBox="0 0 160 80" className="lp-art h-full w-full" aria-hidden>
        {[[20, 18], [140, 14], [30, 66], [135, 64], [12, 44]].map(([x, y], i) => (
          <g key={i}>
            <line x1="80" y1="40" x2={x} y2={y} stroke={stroke} strokeWidth="1" className="lp-flow" style={delay(i * 0.2)} />
            <circle cx={x} cy={y} r="3" fill="#7fd1b0" className="lp-node" style={delay(i * 0.45)} />
          </g>
        ))}
        <rect x="68" y="28" width="24" height="24" rx="4" fill="#1b2b4a" stroke={stroke} strokeWidth="1.5" />
        <rect x="74" y="34" width="12" height="12" rx="2" fill="#7fd1b0" className="lp-chip" />
      </svg>
    );
  if (art === "day")
    return (
      <svg viewBox="0 0 160 80" className="lp-art h-full w-full" aria-hidden>
        {[[22, 48, 18], [42, 40, 16], [62, 44, 20], [82, 30, 22], [102, 36, 18], [122, 22, 24], [142, 30, 14]].map(([x, y, h], i) => (
          <g key={i} className="lp-candle" style={delay(i * 0.3)}>
            <line x1={x} x2={x} y1={y - 8} y2={y + h + 8} stroke={i % 2 ? "#e8805f" : "#7fd1b0"} strokeWidth="1.5" />
            <rect x={x - 5} y={y} width="10" height={h} rx="1.5" fill={i % 2 ? "#e8805f" : "#7fd1b0"} />
          </g>
        ))}
      </svg>
    );
  if (art === "options")
    return (
      <div className="lp-art flex h-full items-center justify-center gap-2 sm:gap-3">
        <span className="lp-turn inline-flex items-center gap-1 rounded-md bg-[#2f6b55] px-2.5 py-1.5 text-xs font-bold tracking-wide text-white sm:px-4 sm:py-2 sm:text-sm">
          CALL <span className="lp-arrow-up text-[#9fe3c4]">▲</span>
        </span>
        <span className="lp-turn inline-flex items-center gap-1 rounded-md bg-[#6b4a3a] px-2.5 py-1.5 text-xs font-bold tracking-wide text-white sm:px-4 sm:py-2 sm:text-sm" style={delay(1.5)}>
          PUT <span className="lp-arrow-down text-[#f2a98f]">▼</span>
        </span>
      </div>
    );
  if (art === "futures")
    return (
      <div className="lp-art flex h-full items-center justify-center gap-2.5">
        {[0, 1, 2].map((i) => (
          <div
            key={i}
            className="lp-lift flex h-14 w-9 flex-col justify-center gap-1.5 rounded border border-white/60 px-1.5"
            style={delay(i * 0.5)}
          >
            <span className="lp-fill h-px bg-white/80" style={delay(i * 0.5)} />
            <span className="lp-fill h-px bg-white/80" style={delay(i * 0.5 + 0.25)} />
          </div>
        ))}
      </div>
    );
  return (
    <svg viewBox="0 0 160 80" className="lp-art h-full w-full" aria-hidden>
      <path d="M80 14 L104 23 V42 C104 56 93 64 80 69 C67 64 56 56 56 42 V23 Z" fill="#7fd1b0" className="lp-glow" />
      {/* The finished drawing stays faintly visible underneath, so the card
          is never empty while the animation redraws it. */}
      <path d="M80 14 L104 23 V42 C104 56 93 64 80 69 C67 64 56 56 56 42 V23 Z" fill="none" stroke={stroke} strokeWidth="1.75" opacity="0.3" />
      <path d="M70 41 L77 48 L91 34" fill="none" stroke="#ffffff" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round" opacity="0.3" />
      <path d="M80 14 L104 23 V42 C104 56 93 64 80 69 C67 64 56 56 56 42 V23 Z" fill="none" stroke={stroke} strokeWidth="1.75" pathLength={1} className="lp-draw" />
      <path d="M70 41 L77 48 L91 34" fill="none" stroke="#ffffff" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round" pathLength={1} className="lp-draw-late" />
    </svg>
  );
};

/**
 * The practice cards' chart: a grid drifting by, the line drawing itself, the
 * area under it fading in, and a pulsing dot on the latest price. Same line
 * as before, now moving.
 */
const LineSpark = ({ points, id }: { points: string; id: string }) => {
  const pts = points.split(" ").map((p) => p.split(",").map(Number));
  const [lx, ly] = pts[pts.length - 1];
  const line = `M${pts.map(([x, y]) => `${x},${y}`).join(" L")}`;
  const area = `${line} L300,90 L0,90 Z`;
  return (
    <div className="lp-art relative h-full w-full">
    <svg viewBox="0 0 300 90" preserveAspectRatio="none" className="h-full w-full overflow-visible" aria-hidden>
      <defs>
        <linearGradient id={`lp-area-${id}`} x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor="#7fd1b0" stopOpacity="0.35" />
          <stop offset="100%" stopColor="#7fd1b0" stopOpacity="0" />
        </linearGradient>
      </defs>
      <g className="lp-grid">
        {Array.from({ length: 12 }, (_, i) => (
          <line key={i} x1={i * 30} x2={i * 30} y1="0" y2="90" stroke="rgba(255,255,255,0.06)" />
        ))}
      </g>
      {[30, 60].map((y) => (
        <line key={y} x1="0" x2="300" y1={y} y2={y} stroke="rgba(255,255,255,0.06)" />
      ))}
      <path d={area} fill={`url(#lp-area-${id})`} className="lp-area" />
      <path d={line} fill="none" stroke="rgba(255,255,255,0.25)" strokeWidth="2.5" strokeLinejoin="round" />
      <path
        d={line}
        fill="none"
        stroke="rgba(255,255,255,0.9)"
        strokeWidth="2.5"
        strokeLinejoin="round"
        pathLength={1}
        className="lp-line"
      />
    </svg>
      {/* The latest price. Outside the SVG so the stretched chart does not
          squash it into an oval. */}
      <span
        className="lp-dot absolute h-2.5 w-2.5 -translate-x-1/2 -translate-y-1/2"
        style={{ left: `${(lx / 300) * 100}%`, top: `${(ly / 90) * 100}%` }}
        aria-hidden
      >
        <span className="lp-ping absolute inset-0 rounded-full bg-[#7fd1b0]" />
        <span className="absolute inset-0 rounded-full bg-white shadow-[0_0_8px_rgba(127,209,176,0.9)]" />
      </span>
    </div>
  );
};

/** Smooth-scroll to the form and put the cursor in the first field. */
const scrollToForm = () => {
  const el = document.getElementById(FORM_ID);
  if (!el) return;
  const reduced =
    typeof window !== "undefined" &&
    window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
  el.scrollIntoView({ behavior: reduced ? "auto" : "smooth", block: "start" });
  // After the scroll settles, so focusing does not fight the animation.
  window.setTimeout(() => {
    document.getElementById("lp-first-name")?.focus({ preventScroll: true });
  }, reduced ? 0 : 600);
};

/**
 * A call to action that jumps to the form.
 *
 * Three buttons on this page carry the same words on purpose — repeating one
 * offer converts better than inventing three. Only one of them submits,
 * though, so the two shortcuts spell that out in their accessible name: a
 * screen reader otherwise announces three identical "Book your free call"
 * buttons with no way to tell which one sends the form. The visible label is
 * the start of the accessible name, so the two still match (WCAG 2.5.3).
 */
const CtaButton = ({
  children,
  className = "",
  ...props
}: React.ComponentProps<typeof Button>) => (
  <Button
    onClick={scrollToForm}
    size="lg"
    aria-label="Book your free call, go to the form"
    className={`group h-12 px-6 text-base font-semibold text-white shadow-lg shadow-[#c4532d]/25 bg-[#c4532d] hover:bg-[#ad4624] ${className}`}
    {...props}
  >
    {children}
    <ArrowRight size={18} className="ml-2 transition-transform group-hover:translate-x-1" />
  </Button>
);

/**
 * The sticky call to action.
 *
 * Hidden until the visitor is past the hero (where a full-size CTA is already
 * on screen) and hidden again once the form itself is visible, so it never
 * covers the fields it is asking people to fill in.
 */
const StickyCta = ({ hidden }: { hidden: boolean }) => (
  <div
    data-testid="lp-sticky-cta"
    aria-hidden={hidden}
    className={`fixed inset-x-0 bottom-0 z-40 px-3 pb-3 transition-all duration-300 sm:inset-x-auto sm:right-6 sm:bottom-6 sm:px-0 sm:pb-0 ${
      hidden ? "pointer-events-none translate-y-6 opacity-0" : "translate-y-0 opacity-100"
    }`}
  >
    <div className="mx-auto flex max-w-md items-center gap-3 rounded-2xl border border-white/60 bg-white/95 p-2.5 shadow-2xl shadow-primary/20 backdrop-blur sm:max-w-none sm:rounded-full sm:p-2 sm:pl-5">
      <span className="hidden text-sm font-medium text-primary sm:block">
        Free consultation call
      </span>
      <Button
        onClick={scrollToForm}
        tabIndex={hidden ? -1 : 0}
        aria-label="Book your free call, go to the form"
        className="h-12 w-full rounded-xl px-6 text-base font-semibold text-white shadow-md bg-[#c4532d] hover:bg-[#ad4624] sm:w-auto sm:rounded-full"
      >
        Book your free call
        <ArrowRight size={17} className="ml-2" />
      </Button>
    </div>
  </div>
);

type FormState = {
  first_name: string;
  last_name: string;
  phone: string;
  email: string;
  experience_level: ExperienceValue | "";
  /** "I agree to be contacted". Gates the submit; not sent to the CRM. */
  consent: boolean;
};

const EMPTY_FORM: FormState = {
  first_name: "",
  last_name: "",
  phone: "",
  email: "",
  experience_level: "",
  consent: false,
};

const Lp = () => {
  const { search } = useLocation();
  const navigate = useNavigate();
  // The members section renders only what is real: published videos and the
  // checked-in Google reviews.
  const { data: memberVideos } = usePublishedTestimonials();
  const hasVideos = !!memberVideos?.length;
  const hasReviews = GOOGLE_REVIEWS.length > 0;

  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [errors, setErrors] = useState<Partial<Record<keyof FormState, string>>>({});
  const [loading, setLoading] = useState(false);
  const [navigating, setNavigating] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const [scrolled, setScrolled] = useState(false);
  const [formInView, setFormInView] = useState(false);
  /**
   * When this page was opened, which is the whole of what is left of the bot
   * defence.
   *
   * There used to be a hidden input here that a bot would fill and a person
   * would not. Password managers fill it too — a real visitor reported the
   * form dead, and the console said the trap had caught them. Renaming the
   * field to something meaningless and adding the LastPass, 1Password and
   * Dashlane opt-outs did not stop it either: managers fill every text input
   * in a form, and no attribute reliably says otherwise.
   *
   * So there is no hidden field any more. Nothing on this page can refuse a
   * submission from someone who filled it in — on a page paid for by the
   * click, a lead that vanishes is worse than a spam lead the CRM can delete.
   * How long the form took is stamped on the lead instead, and the CRM can
   * sort by it: a person needs several seconds for four fields, and anything
   * under two is worth a second look by whoever works the queue.
   */
  const openedAt = useRef(Date.now());

  const heroCtaRef = useRef<HTMLDivElement>(null);
  const formRef = useRef<HTMLElement>(null);

  /**
   * The sticky CTA appears once the hero's own "Book my free call" has been
   * scrolled past, and not before: while that button is on screen there is
   * already a full-size way to the form, and a second one pinned to the
   * bottom is just a bar over the page.
   *
   * It watches that button, not the hero section around it. Watching the
   * section meant the mascot and the stats had to leave the viewport too —
   * 1128px on a phone, more than a screen and a half, by which point the page
   * reads as having no sticky call to action at all.
   */
  useEffect(() => {
    const el = heroCtaRef.current;
    if (!el || typeof IntersectionObserver === "undefined") return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        // "Not intersecting" is not the same as "scrolled past". On a phone
        // the hero's own button starts below the fold — the mascot and the
        // headline push it to 676px on a 664px screen — and an element that
        // has never been on screen reports exactly what one that has gone off
        // the top reports. Reading the first as the second put the sticky bar
        // on the page from the moment it loaded, over the hero it was meant
        // to wait behind. The rect is what tells the two apart: the button is
        // behind us only once its bottom edge is above the top of the screen.
        setScrolled(!entry.isIntersecting && entry.boundingClientRect.bottom <= 0);
      },
      { threshold: 0 },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  // ...and disappears while the form is on screen, so it never sits on top of it.
  useEffect(() => {
    const el = formRef.current;
    if (!el || typeof IntersectionObserver === "undefined") return;
    const obs = new IntersectionObserver(
      ([entry]) => setFormInView(entry.isIntersecting),
      { threshold: 0.15 },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  /**
   * Where the lead came from, stamped on the lead itself. Without it a campaign
   * lead is indistinguishable from an organic one in the CRM, which makes the
   * ad spend impossible to judge.
   */
  const attribution = useMemo(
    () => buildAttribution("Landing Page /lp", search),
    [search],
  );

  const update = <K extends keyof FormState>(field: K, value: FormState[K]) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => (prev[field] ? { ...prev, [field]: undefined } : prev));
  };

  /**
   * Mirrors the CHECK constraints on `public.leads` (name 1–100, email shape,
   * phone 7–20). The database rejects anything outside them, and a rejected
   * insert reads to the visitor as "something went wrong" — so the message
   * that actually names the problem has to come from here.
   */
  const validate = useCallback((values: FormState) => {
    const next: Partial<Record<keyof FormState, string>> = {};
    const firstName = values.first_name.trim();
    const lastName = values.last_name.trim();
    const phone = values.phone.trim();
    const email = values.email.trim();

    // Both, separately. The CRM has always had two fields and this form used
    // to hand it one, leaving the split to a guess on whitespace.
    if (!firstName) next.first_name = "Please enter your first name.";
    else if (firstName.length > 50) next.first_name = "Please use 50 characters or fewer.";

    if (!lastName) next.last_name = "Please enter your last name.";
    else if (lastName.length > 50) next.last_name = "Please use 50 characters or fewer.";

    if (phone.replace(/\D/g, "").length < 7 || phone.length > 20) {
      next.phone = "Please enter a phone number we can reach you on.";
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      next.email = "Please enter a valid email address.";
    } else if (email.length > 255) {
      next.email = "Please use a shorter email address.";
    }

    if (!values.experience_level) next.experience_level = "Pick the option closest to you.";

    if (!values.consent) next.consent = "Please confirm we can contact you about the academy.";

    return next;
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const found = validate(form);
    setErrors(found);
    if (Object.keys(found).length > 0) {
      const firstField = Object.keys(found)[0];
      document.getElementById(`lp-${firstField.replace(/_/g, "-")}`)?.focus();
      return;
    }

    setLoading(true);
    setSubmitError(null);
    // Straight to the CRM, which is the system of record for leads: nothing is
    // written to Supabase from here. The shared module owns the wire format so
    // this page and /enroll cannot drift apart on the CRM side.
    const result = await deliverLeadToCrm({
      first_name: form.first_name,
      last_name: form.last_name,
      email: form.email,
      phone: form.phone,
      experience_level: form.experience_level,
      attribution,
      form_name: "lp",
      landing_page: "/lp",
      search,
      fillSeconds: Math.round((Date.now() - openedAt.current) / 1000),
    });
    setLoading(false);

    // Never show a fake confirmation: a lead that thinks it got through and
    // did not is worse than an honest failure with a way to reach us. The
    // reason is logged rather than shown — a visitor cannot use "crm-http-404",
    // but it is the first thing worth knowing when one of them reports that
    // the form is broken.
    if (!result.delivered) {
      console.error(`[lp] lead not delivered: ${result.reason}`);
      setSubmitError(
        "We could not submit your details. Please try again, or email office@bullio.org directly.",
      );
      return;
    }

    // The confirmation is its own page: it is where campaign conversion
    // tracking fires, and a URL the visitor can be sent back to.
    setForm(EMPTY_FORM);
    setNavigating(true);
    navigate("/thank-you");
  };

  const stickyHidden = !scrolled || formInView || navigating;

  return (
    <div className="min-h-screen bg-[#f6f3ec] pb-24 [--muted-foreground:220_14%_34%] sm:pb-0">
      <SEO
        title="Bullio Academy | Learn to Spot Opportunities"
        description="Expert courses, lectures and masterclasses, live trading rooms and virtual-money practice, with a real person helping you choose your next step. Book a free call."
        noindex
      />

      <MarketTicker />

      {/* Header: logo, in-page links and the call to action. Nothing here
          leaves the page. */}
      <header className="px-4 sm:px-6">
        <div className="mx-auto flex container-page items-center justify-between gap-4 border-b border-[#173f33]/10 py-3">
          <div className="flex min-w-0 items-center gap-2.5">
            <img src={bullioFace} alt="" width={36} height={36} className="h-9 w-9 object-contain" />
            <div className="leading-tight">
              <div className="truncate text-[15px] font-bold text-[#173f33] sm:text-base">Bullio Academy</div>
              <div className="hidden text-[10px] uppercase tracking-[0.16em] text-muted-foreground sm:block">
                Education. Practice. Support.
              </div>
            </div>
          </div>
          <nav className="hidden items-center gap-7 whitespace-nowrap text-sm text-foreground/75 lg:flex" aria-label="On this page">
            {[
              ["Courses", "courses"],
              ["Student stories", "stories"],
              ["Your advisor", "advisor"],
            ].map(([label, id]) => (
              <button key={id} type="button" onClick={() => scrollToId(id)} className="py-2 hover:text-[#173f33]">
                {label}
              </button>
            ))}
          </nav>
          <CtaButton className="h-10 shrink-0 px-3.5 text-sm sm:px-5">
            <span className="sm:hidden">Free call</span>
            <span className="hidden sm:inline">Book your free call</span>
          </CtaButton>
        </div>
      </header>

      <main>

      {/* Hero */}
      <section data-testid="lp-hero" className="px-4 pb-10 pt-8 sm:px-6 sm:pb-14 sm:pt-12">
        <div className="mx-auto grid container-page items-center gap-10 lg:grid-cols-[1.1fr_0.9fr] lg:gap-14">
          <div>
            <span className="inline-flex rounded-md bg-[#173f33]/[0.07] px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.12em] text-[#173f33]">
              For every starting point
            </span>
            <h1 className="mt-5 text-[2.3rem] font-bold leading-[1.08] tracking-tight text-primary sm:text-5xl lg:text-[3.4rem]" style={{ textWrap: "balance" }}>
              Learn to spot opportunities.
              <span className="block text-[#173f33]">Start with the right support.</span>
            </h1>
            <p className="mt-5 max-w-xl text-[15px] leading-relaxed text-foreground/75 sm:text-base">
              Expert courses, lectures and masterclasses by leading names in trading education, plus
              live trading rooms and virtual-money practice, with a real person helping you choose
              your next step.
            </p>
            <div className="mt-5 flex items-start gap-2.5">
              <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#173f33] text-white">
                <Check size={12} strokeWidth={3} />
              </span>
              <div>
                <div className="text-[15px] font-semibold text-primary">No experience? Start with the basics.</div>
                <div className="text-sm text-muted-foreground">Already trading? Build on what you know.</div>
              </div>
            </div>
            <div ref={heroCtaRef} data-testid="lp-hero-cta" className="mt-7 flex flex-col gap-4 sm:flex-row sm:items-center">
              <CtaButton className="w-full sm:w-auto">Book your free call</CtaButton>
              <div className="text-xs leading-snug">
                <div className="font-semibold text-foreground/80">Free call. No obligation to enroll.</div>
                <div className="text-muted-foreground">Academy enrollment is a separate paid choice.</div>
              </div>
            </div>
          </div>

          <div>
            <div className="relative overflow-hidden rounded-3xl shadow-2xl shadow-[#173f33]/10 ring-1 ring-black/5">
              <img
                src={heroLearner}
                srcSet={`${heroLearner480} 480w, ${heroLearner680} 680w, ${heroLearner} 860w`}
                sizes="(min-width: 1024px) 522px, (min-width: 640px) 720px, calc(100vw - 32px)"
                alt="A learner thinking through a lesson at his laptop"
                width={860}
                height={720}
                fetchPriority="high"
                decoding="async"
                className="aspect-[860/720] w-full object-cover"
              />
              <div className="absolute inset-x-4 bottom-4 rounded-xl bg-[#173f33]/95 px-4 py-3 text-white shadow-lg sm:inset-x-6 sm:bottom-6">
                <div className="font-semibold">A clear plan. Someone to ask.</div>
                <div className="text-xs text-white/75">Learn at your own pace.</div>
              </div>
            </div>
            <p className="mt-2 text-[11px] text-muted-foreground">Illustrative learning scene, not a customer review.</p>
          </div>
        </div>
      </section>

      {/* Five highlights */}
      <section className="bg-[#173f33] px-4 text-white sm:px-6">
        <div className="mx-auto grid container-page grid-cols-2 gap-y-5 py-6 sm:grid-cols-3 lg:grid-cols-5 lg:divide-x lg:divide-white/15">
          {highlights.map((h, i) => (
            <div key={h.title} className={`px-2 lg:px-5 ${i === 4 ? "col-span-2 sm:col-span-1" : ""}`}>
              <div className="flex items-center gap-2 text-sm font-semibold">
                <h.icon size={17} className="shrink-0 text-[#9fd8bf]" />
                {h.title}
              </div>
              <div className="mt-1 text-xs text-white/70">{h.desc}</div>
            </div>
          ))}
        </div>
      </section>

      {(hasVideos || hasReviews) && (
        <section id="stories" data-testid="lp-members" className="scroll-mt-4 px-4 py-14 sm:px-6 sm:py-20">
          <div className="mx-auto container-page">
            <Reveal>
              <Kicker>Student videos &amp; reviews</Kicker>
              <h2 className="mt-3 text-3xl font-bold text-primary sm:text-4xl">Hear about the experience.</h2>
              <p className="mt-3 text-[15px] text-muted-foreground sm:text-base">
                Real students, in their own words and on camera.
              </p>
            </Reveal>

            {hasVideos && <VideoTestimonials limit={3} showViewAll={false} className="mt-8" />}

            {hasReviews && (
              /* Says where they came from without repeating the hero's
                 rating, which counts every review rather than these eight. */
              <Reveal>
                <div
                  className={`flex flex-wrap items-center justify-center gap-x-2.5 gap-y-1 px-4 text-center text-sm font-medium text-muted-foreground ${
                    hasVideos ? "mt-14" : "mt-10"
                  }`}
                >
                  <GoogleGlyph className="h-4 w-4" />
                  Reviews from our Google Business Profile
                </div>
              </Reveal>
            )}
          </div>

          {/* Outside the page container, and pulling back the section's own
              padding, so the row runs to both edges of the screen: a marquee
              that stops short of the viewport looks like a widget rather than
              a feed that carries on past the frame. */}
          {hasReviews && (
            <GoogleReviewMarquee
              reviews={GOOGLE_REVIEWS}
              className="-mx-4 mt-8 sm:-mx-6"
              // How the review images are re-made: see the prop's own note.
              asMarkup={new URLSearchParams(search).get("cards") === "markup"}
              markupWidth={Number(new URLSearchParams(search).get("w")) || 520}
            />
          )}
        </section>
      )}


      {/* Topics */}
      <section id="courses" className="scroll-mt-4 px-4 pb-14 sm:px-6 sm:pb-20">
        <div className="mx-auto container-page">
          <Reveal>
            <Kicker>Explore the courses</Kicker>
            <h2 className="mt-3 text-3xl font-bold text-primary sm:text-4xl">Build skills. Explore new possibilities.</h2>
            <p className="mt-3 text-[15px] text-muted-foreground sm:text-base">
              Choose a subject that interests you. Your advisor helps you find the right starting level.
            </p>
          </Reveal>
          <div className="mt-8 grid grid-cols-2 gap-3 sm:gap-4 md:grid-cols-3 lg:grid-cols-5">
            {topics.map((t, i) => (
              <Reveal key={t.title} delay={i * 60} className={`h-full ${i === topics.length - 1 ? "col-span-2 md:col-span-1" : ""}`}>
                <button
                  type="button"
                  onClick={scrollToForm}
                  className="group flex h-full w-full flex-col overflow-hidden rounded-2xl border border-[#173f33]/10 bg-white text-left shadow-sm transition-all hover-lift"
                >
                  <div className={`h-24 p-3 sm:h-28 ${t.tone === "navy" ? "bg-[#1b2b4a]" : "bg-[#173f33]"}`}>
                    <TopicArt art={t.art} />
                  </div>
                  <div className="flex flex-1 flex-col p-3.5 sm:p-4">
                    <h3 className="text-[15px] font-semibold leading-snug text-primary sm:text-base">{t.title}</h3>
                    <p className="mt-1.5 flex-1 text-[13px] leading-relaxed text-muted-foreground sm:mt-2 sm:text-sm">{t.desc}</p>
                    <span className="mt-4 flex items-center justify-between text-xs font-semibold text-foreground/80">
                      Explore topic
                      <ArrowRight size={15} className="transition-transform group-hover:translate-x-1" />
                    </span>
                  </div>
                </button>
              </Reveal>
            ))}
          </div>

          <div className="mt-5 flex items-start gap-4 rounded-2xl bg-[#173f33]/[0.08] p-5 sm:p-6">
            <BookOpen size={26} strokeWidth={1.6} className="mt-0.5 shrink-0 text-[#173f33]" />
            <div>
              <h3 className="text-lg font-bold text-[#173f33] sm:text-xl">Never placed a trade? Start here.</h3>
              <p className="mt-1.5 text-sm leading-relaxed text-foreground/70">
                Begin with Trading Fundamentals. Learn the basics, then practise with virtual money. No
                need to jump into advanced topics on day one.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Advisor */}
      <section id="advisor" className="scroll-mt-4 bg-[#173f33] px-4 py-14 text-white sm:px-6 sm:py-20">
        <div className="mx-auto grid container-page items-center gap-10 lg:grid-cols-[1.2fr_1fr]">
          <div>
            <Kicker light>Your education advisor</Kicker>
            <h2 className="mt-3 text-3xl font-bold text-white sm:text-4xl">
              Not just more videos.
              <span className="block text-[#9fd8bf]">Someone to help it click.</span>
            </h2>
            <ul className="mt-6 space-y-3">
              {advisorPoints.map((p) => (
                <li key={p} className="flex items-start gap-2.5 text-[15px] text-white/85">
                  <Check size={16} strokeWidth={3} className="mt-1 shrink-0 text-[#9fd8bf]" />
                  {p}
                </li>
              ))}
            </ul>
          </div>
          <div className="rounded-2xl bg-white p-5 text-foreground shadow-xl sm:p-6">
            <div className="flex items-center gap-2.5 text-lg font-semibold text-[#173f33]">
              <User size={20} />
              Your next steps
            </div>
            <ol className="mt-4 space-y-2.5">
              {nextSteps.map((s, i) => (
                <li
                  key={s.title}
                  className={`flex gap-3 rounded-xl px-4 py-3 ${i === 0 ? "bg-[#173f33] text-white" : "bg-[#173f33]/[0.07]"}`}
                >
                  <span className={`text-xs font-bold ${i === 0 ? "text-white/80" : "text-[#173f33]"}`}>
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <div>
                    <div className={`text-sm font-semibold ${i === 0 ? "" : "text-[#173f33]"}`}>{s.title}</div>
                    <div className={`text-xs ${i === 0 ? "text-white/75" : "text-muted-foreground"}`}>{s.desc}</div>
                  </div>
                </li>
              ))}
            </ol>
            <p className="mt-3 text-[11px] text-muted-foreground">Illustrative learning path.</p>
          </div>
        </div>
      </section>

      {/* Practice */}
      <section className="px-4 py-14 sm:px-6 sm:py-20">
        <div className="mx-auto container-page">
          <Kicker>Watch it. Try it. Talk it through.</Kicker>
          <h2 className="mt-3 text-3xl font-bold text-primary sm:text-4xl">Turn the lessons into practice.</h2>
          <div className="mt-8 grid gap-5 md:grid-cols-2">
            {[
              {
                tag: "Live trading room",
                title: "See the thinking behind a trade.",
                desc: "Watch educators explain market examples, decisions and risk.",
                points: "0,70 30,52 55,62 85,30 115,55 145,40 175,62 205,35 235,48 270,28 300,40",
                live: true,
              },
              {
                tag: "Demo account · virtual money",
                title: "Try it with virtual money.",
                desc: "Practise the lesson without risking real money on those demo trades.",
                points: "0,75 35,60 60,62 90,40 120,52 150,30 180,45 210,58 240,35 270,42 300,38",
                live: false,
              },
            ].map((c) => (
              <div key={c.title} className="overflow-hidden rounded-2xl border border-[#173f33]/10 bg-white shadow-sm">
                <div className="relative h-40 bg-[#173f33] p-4">
                  <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider text-white/80">
                    {c.live && (
                      <span className="inline-flex items-center gap-1 rounded bg-red-600 px-1.5 py-0.5 text-[10px] text-white">
                        <span className="h-1.5 w-1.5 rounded-full bg-white" /> Live
                      </span>
                    )}
                    {c.tag}
                  </div>
                  <div className="absolute inset-x-4 bottom-4 top-12">
                    <LineSpark points={c.points} id={c.live ? "live" : "demo"} />
                  </div>
                </div>
                <div className="p-5">
                  <h3 className="text-lg font-bold text-primary">{c.title}</h3>
                  <p className="mt-1.5 text-sm text-muted-foreground">{c.desc}</p>
                </div>
              </div>
            ))}
          </div>

          {/* How the call works */}
          <div className="mt-8 rounded-2xl bg-[#173f33]/[0.08] p-6 sm:p-8">
            <h2 className="text-2xl font-bold text-[#173f33] sm:text-3xl">A free conversation. A clear next step.</h2>
            <ol className="mt-6 grid grid-cols-2 gap-x-4 gap-y-6 lg:grid-cols-4">
              {callSteps.map((s, i) => (
                <li key={s.title} className="relative">
                  <span className="flex h-7 w-7 items-center justify-center rounded-full bg-[#c4532d] text-xs font-bold text-white">
                    {i + 1}
                  </span>
                  {i < callSteps.length - 1 && (
                    <ArrowRight size={16} className="absolute right-2 top-1.5 hidden text-[#173f33]/50 lg:block" />
                  )}
                  <div className="mt-3 text-[15px] font-semibold text-[#173f33]">{s.title}</div>
                  <div className="mt-0.5 text-sm text-muted-foreground">{s.desc}</div>
                </li>
              ))}
            </ol>
          </div>

          {/* FAQ */}
          <div className="mt-12">
            <Kicker>A few things worth knowing</Kicker>
            <dl className="mt-5 grid gap-x-12 gap-y-6 md:grid-cols-2">
              {faqs.map((f) => (
                <div key={f.q}>
                  <dt className="font-semibold text-primary">{f.q}</dt>
                  <dd className="mt-1 text-sm leading-relaxed text-muted-foreground">{f.a}</dd>
                </div>
              ))}
            </dl>
          </div>
        </div>
      </section>

      {/* The form, the only conversion on the page */}
      <section id={FORM_ID} ref={formRef} className="scroll-mt-6 px-4 pb-14 sm:px-6 sm:pb-20">
        <div className="mx-auto grid container-page gap-10 rounded-3xl bg-[#173f33] p-6 text-white shadow-xl sm:p-10 lg:grid-cols-[0.9fr_1.1fr]">
          <div className="flex flex-col">
            <Kicker light>Your next step starts here</Kicker>
            <h2 className="mt-3 text-3xl font-bold text-white sm:text-4xl">Find your starting point.</h2>
            <p className="mt-4 max-w-md text-[15px] leading-relaxed text-white/80 sm:text-base">
              Tell us what you want to learn. We'll explain Bullio, answer your questions and help you
              see where to begin.
            </p>
            <p className="mt-4 font-semibold">The call is free. No obligation to enroll.</p>
            <div className="mt-6 hidden h-40 w-28 overflow-hidden rounded-2xl bg-[#f6f3ec] lg:block">
              <img src={bullioHero} srcSet={`${bullioHero360} 360w, ${bullioHero} 640w`} sizes="112px" alt="" width={640} height={1163} loading="lazy" className="h-full w-full object-contain object-bottom pt-2" />
            </div>
          </div>
          <div>
              <form onSubmit={handleSubmit} noValidate className="space-y-5">
                {/* First and last, each its own field. The CRM has always
                    stored them apart, and one box marked "Full name" meant
                    the split was guessed from the whitespace — which reads
                    "Mary Jane Watson" as a surname of "Jane Watson", and
                    gives a one-word answer no surname at all. Side by side on
                    anything but the narrowest phone, so the form is no longer
                    to scroll. */}
                <div className="grid gap-5 sm:grid-cols-2">
                  <div>
                    <label htmlFor="lp-first-name" className="text-sm font-medium text-white/90">
                      First name
                    </label>
                    <input
                      id="lp-first-name"
                      name="first_name"
                      type="text"
                      autoComplete="given-name"
                      required
                      maxLength={50}
                      value={form.first_name}
                      onChange={(e) => update("first_name", e.target.value)}
                      aria-invalid={!!errors.first_name}
                      aria-describedby={errors.first_name ? "lp-first-name-error" : undefined}
                      className={`mt-1.5 w-full rounded-lg border bg-white px-3.5 py-3 text-sm text-foreground outline-none transition-colors focus:border-secondary focus:ring-4 focus:ring-secondary/15 ${
                        errors.first_name ? "border-red-300 ring-2 ring-red-300/40" : "border-white/20"
                      }`}
                      placeholder="Jane"
                    />
                    {errors.first_name && (
                      <p id="lp-first-name-error" className="mt-1.5 text-xs text-red-200">
                        {errors.first_name}
                      </p>
                    )}
                  </div>

                  <div>
                    <label htmlFor="lp-last-name" className="text-sm font-medium text-white/90">
                      Last name
                    </label>
                    <input
                      id="lp-last-name"
                      name="last_name"
                      type="text"
                      autoComplete="family-name"
                      required
                      maxLength={50}
                      value={form.last_name}
                      onChange={(e) => update("last_name", e.target.value)}
                      aria-invalid={!!errors.last_name}
                      aria-describedby={errors.last_name ? "lp-last-name-error" : undefined}
                      className={`mt-1.5 w-full rounded-lg border bg-white px-3.5 py-3 text-sm text-foreground outline-none transition-colors focus:border-secondary focus:ring-4 focus:ring-secondary/15 ${
                        errors.last_name ? "border-red-300 ring-2 ring-red-300/40" : "border-white/20"
                      }`}
                      placeholder="Cohen"
                    />
                    {errors.last_name && (
                      <p id="lp-last-name-error" className="mt-1.5 text-xs text-red-200">
                        {errors.last_name}
                      </p>
                    )}
                  </div>
                </div>

                <div className="grid gap-5 sm:grid-cols-2">
                  <div>
                    <label htmlFor="lp-phone" className="text-sm font-medium text-white/90">
                      Phone
                    </label>
                    <input
                      id="lp-phone"
                      name="phone"
                      type="tel"
                      inputMode="tel"
                      autoComplete="tel"
                      required
                      maxLength={20}
                      value={form.phone}
                      onChange={(e) => update("phone", e.target.value)}
                      aria-invalid={!!errors.phone}
                      aria-describedby={errors.phone ? "lp-phone-error" : undefined}
                      className={`mt-1.5 w-full rounded-lg border bg-white px-3.5 py-3 text-sm text-foreground outline-none transition-colors focus:border-secondary focus:ring-4 focus:ring-secondary/15 ${
                        errors.phone ? "border-red-300 ring-2 ring-red-300/40" : "border-white/20"
                      }`}
                      placeholder="+1 555 000 0000"
                    />
                    {errors.phone && (
                      <p id="lp-phone-error" className="mt-1.5 text-xs text-red-200">
                        {errors.phone}
                      </p>
                    )}
                  </div>

                  <div>
                    <label htmlFor="lp-email" className="text-sm font-medium text-white/90">
                      Email
                    </label>
                    <input
                      id="lp-email"
                      name="email"
                      type="email"
                      autoComplete="email"
                      required
                      maxLength={255}
                      value={form.email}
                      onChange={(e) => update("email", e.target.value)}
                      aria-invalid={!!errors.email}
                      aria-describedby={errors.email ? "lp-email-error" : undefined}
                      className={`mt-1.5 w-full rounded-lg border bg-white px-3.5 py-3 text-sm text-foreground outline-none transition-colors focus:border-secondary focus:ring-4 focus:ring-secondary/15 ${
                        errors.email ? "border-red-300 ring-2 ring-red-300/40" : "border-white/20"
                      }`}
                      placeholder="you@example.com"
                    />
                    {errors.email && (
                      <p id="lp-email-error" className="mt-1.5 text-xs text-red-200">
                        {errors.email}
                      </p>
                    )}
                  </div>
                </div>

                {/*
                  The fourth question. Experience level is the one answer that
                  changes how the call opens, it is a single tap rather than
                  typing, and it lands in the `experience_level` column the
                  admin lead list already filters and exports on.
                */}
                <fieldset
                  aria-describedby={
                    errors.experience_level ? "lp-experience-level-error" : undefined
                  }
                >
                  <legend className="text-sm font-medium text-white/90">
                    Where are you in your trading today?
                  </legend>
                  {/*
                    Real radio inputs, visually hidden behind the cards. A
                    div of role="radio" buttons looks identical and loses
                    arrow-key navigation and the single tab stop that a
                    native group gives for free.
                  */}
                  <div className="mt-2.5 grid gap-2.5 sm:grid-cols-2">
                    {EXPERIENCE_OPTIONS.map((opt, i) => {
                      const active = form.experience_level === opt.value;
                      return (
                        <label
                          key={opt.value}
                          className={`cursor-pointer rounded-lg border p-3.5 transition-all focus-within:ring-4 focus-within:ring-white/20 ${
                            active
                              ? "border-white bg-white/10 ring-1 ring-white/40"
                              : errors.experience_level
                                ? "border-red-300 hover:border-white/50"
                                : "border-white/20 hover:border-white/50 hover:bg-white/5"
                          }`}
                        >
                          <input
                            type="radio"
                            name="experience_level"
                            value={opt.value}
                            id={i === 0 ? "lp-experience-level" : undefined}
                            checked={active}
                            onChange={() => update("experience_level", opt.value)}
                            className="sr-only"
                          />
                          <span className="flex items-center gap-2 text-sm font-semibold text-white">
                            <span
                              className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-full border-2 ${
                                active ? "border-white bg-white" : "border-white/50"
                              }`}
                            >
                              {active && <Check size={10} className="text-[#173f33]" strokeWidth={4} />}
                            </span>
                            {opt.label}
                          </span>
                          <span className="mt-1 block pl-6 text-xs leading-snug text-white/60">
                            {opt.hint}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                  {errors.experience_level && (
                    <p id="lp-experience-level-error" className="mt-1.5 text-xs text-red-200">
                      {errors.experience_level}
                    </p>
                  )}
                </fieldset>

                <div>
                  <label htmlFor="lp-consent" className="flex cursor-pointer items-start gap-3 py-1 text-sm text-white/85">
                    <input
                      id="lp-consent"
                      name="consent"
                      type="checkbox"
                      required
                      checked={form.consent}
                      onChange={(e) => update("consent", e.target.checked)}
                      aria-invalid={!!errors.consent}
                      aria-describedby={errors.consent ? "lp-consent-error" : undefined}
                      className="mt-0.5 h-5 w-5 shrink-0 rounded border-white/40 accent-[#c4532d]"
                    />
                    I agree to be contacted about Bullio's paid academy.
                  </label>
                  {errors.consent && (
                    <p id="lp-consent-error" className="mt-1.5 text-xs text-red-200">
                      {errors.consent}
                    </p>
                  )}
                </div>

                {submitError && (
                  <p
                    role="alert"
                    className="rounded-lg border border-red-300/40 bg-red-500/15 px-4 py-3 text-sm text-red-100"
                  >
                    {submitError}
                  </p>
                )}

                <Button
                  type="submit"
                  disabled={loading}
                  className="group h-14 w-full justify-between px-6 text-base font-semibold text-white shadow-lg bg-[#c4532d] hover:bg-[#ad4624]"
                >
                  {loading ? "Sending…" : "Book your free call"}
                  <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
                </Button>

                <p className="text-xs leading-relaxed text-white/60">
                  No payment is taken here. Privacy policy applies.
                </p>
              </form>
          </div>
        </div>
      </section>
      </main>

            <footer className="px-4 pb-10 sm:px-6">
        <div className="mx-auto container-page border-t border-[#173f33]/15 pt-8">
          <div className="flex flex-col gap-6 md:flex-row md:items-start md:justify-between">
            <div>
              <div className="text-lg font-bold text-[#173f33]">Bullio Academy</div>
              <div className="text-xs text-muted-foreground">Education. Practice. Support.</div>
            </div>
            <div className="max-w-xl md:text-right">
          <p className="text-xs leading-relaxed text-muted-foreground">
            Trading involves risk of loss. Profits are not guaranteed. Demo results do not predict
            real-money results. Bullio Academy provides trading education only, and nothing on this
            page is investment advice.
          </p>
          {/* The links are padded to a thumb's height rather than left at the
              16px of their own text. It changes nothing about how the strip
              looks — the words sit where they did — only what a finger on a
              phone can actually land on. */}
          <div className="mt-1 flex flex-wrap items-center gap-x-5 text-xs text-muted-foreground md:justify-end">
            <span>© {new Date().getFullYear()} Bullio Academy</span>
            <a
              href="/privacy"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex min-h-[44px] items-center hover:text-foreground"
            >
              Privacy
            </a>
            <a
              href="/terms"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex min-h-[44px] items-center hover:text-foreground"
            >
              Terms
            </a>
            <a
              href="mailto:office@bullio.org"
              className="inline-flex min-h-[44px] items-center hover:text-foreground"
            >
              office@bullio.org
            </a>
          </div>
            </div>
          </div>
        </div>
      </footer>

      <StickyCta hidden={stickyHidden} />
    </div>
  );
};

export default Lp;
